// Copyright (c) 2020 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage } from "react-intl";
import { Button } from "react-bootstrap";

import { IAction, ActionType, raiseAction } from "actions";
import { IState } from "states/states";
import { AppState } from "states/schedulerstates";

import AppStateView from "view/comp/AppState";
import LoadingAnimation from "./LoadingAnimation";
import { isSet } from "shared/utils/utils";

import "./SiteAnimation.scss";

const CONNECTION_TIMEOUT = 10; // s, after which the offline link will be shown

interface ISiteAnimationExtProps {}
interface ISiteAnimationProps {
  appState: AppState;
  siteConnected: number | undefined;
  canDisconnect: boolean;
}

const mapStateToProps = (state: IState) => {
  const props: ISiteAnimationProps = {
    appState: state.scheduler.appState,
    siteConnected: state.cached.selectedSite,
    // a single site gets auto-selected again right after disconnecting
    canDisconnect: state.site.siteList?.length !== 1,
  };
  return props;
};

interface ISiteAnimationState {
  lastState: AppState;
  lastStateSwitch: number | undefined;
}

interface ISiteAnimationDispatch {
  onDisconnectClick: () => void;
  onGoOfflineClick: () => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: ISiteAnimationDispatch = {
    onDisconnectClick: () => {
      dispatch(raiseAction(ActionType.ACT_DISCONNECT_SITE));
    },
    onGoOfflineClick: () => {
      dispatch(raiseAction(ActionType.ACT_INIT_DONE));
    },
  };
  return dispatchProps;
};

class SiteAnimationComponent extends React.Component<
  ISiteAnimationProps & ISiteAnimationExtProps & ISiteAnimationDispatch,
  ISiteAnimationState
> {
  private refreshTimeout: ReturnType<typeof setTimeout> | undefined;

  state: ISiteAnimationState = {
    lastState: AppState.INIT,
    lastStateSwitch: undefined,
  };

  private clearRefreshTimeout() {
    if (this.refreshTimeout !== undefined) {
      clearTimeout(this.refreshTimeout);
      this.refreshTimeout = undefined;
    }
  }
  public componentWillUnmount() {
    this.clearRefreshTimeout();
  }

  public componentDidUpdate() {
    if (this.props.appState !== this.state.lastState) {
      this.setState({ lastState: this.props.appState, lastStateSwitch: Date.now() });
    }
    if (this.props.appState === AppState.LIVE_DATA_SUBSCRIBED) {
      this.clearRefreshTimeout();
      this.refreshTimeout = setTimeout(() => {
        this.refreshTimeout = undefined;
        // ensure state update to recheck after 1 s
        this.setState({});
      }, 1000);
    } else {
      this.clearRefreshTimeout();
    }
  }

  public render() {
    return (
      <div id="siteanimation">
        <LoadingAnimation />
        <div id="loadingstatus">
          <AppStateView />
          {this.props.appState >= AppState.SITE_SELECTED &&
            isSet(this.props.siteConnected) &&
            this.renderButtons()}
        </div>
      </div>
    );
  }

  private renderButtons() {
    const isConnectTimedout =
      this.props.appState === AppState.LIVE_DATA_SUBSCRIBED &&
      isSet(this.state.lastStateSwitch) &&
      (Date.now() - this.state.lastStateSwitch) / 1000 > CONNECTION_TIMEOUT;

    return (
      <>
        <div className="buttonbox">
          {this.props.canDisconnect && (
            <Button
              id="disconnect"
              variant="secondary"
              type="submit"
              onClick={this.props.onDisconnectClick}
            >
              <FormattedMessage id="Disconnect" />
            </Button>
          )}
          {isConnectTimedout && (
            <Button
              id="gooffline"
              type="submit"
              variant="danger"
              onClick={this.props.onGoOfflineClick}
            >
              <FormattedMessage id="Connect Offline" />
            </Button>
          )}
        </div>
      </>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(SiteAnimationComponent);
